import { useState } from "react";
import { specialTopics, type SpecialTopic, type PollSide } from "@/mocks/heiliaoHome";

function SideOption({
  side,
  totalLabel,
  selected,
  onSelect,
}: {
  side: PollSide;
  totalLabel: string;
  selected: boolean;
  onSelect: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onSelect}
      className={`w-full text-left rounded-md border p-3 md:p-3.5 transition-all cursor-pointer ${
        selected ? "border-primary-400 ring-2 ring-primary-200" : "border-background-300/60 hover:border-background-400"
      }`}
    >
      <div className="flex items-center justify-between gap-2">
        <span className="text-sm font-semibold text-foreground-900">{side.label}</span>
        <span className={`whitespace-nowrap text-sm font-bold ${side.textClass}`}>{side.ratio}%</span>
      </div>
      <div className="mt-2 h-1.5 w-full rounded-full bg-background-200/70 overflow-hidden">
        <div
          className={`h-full rounded-full ${side.barClass} transition-all duration-700`}
          style={{ width: selected ? `${side.ratio}%` : "8%" }}
        ></div>
      </div>
      <div className="mt-2 flex items-center justify-between text-[11px] text-foreground-500">
        <span>{side.votes} 票</span>
        <span>{selected ? "已选 · 谢谢参与" : "点击表态"}</span>
      </div>
    </button>
  );
}

function PollCard({ topic }: { topic: SpecialTopic }) {
  const [selected, setSelected] = useState<"A" | "B" | null>(null);
  return (
    <article className="group flex flex-col overflow-hidden rounded-lg border border-background-200/70 bg-background-50 hover:border-primary-200 transition-colors">
      <div className="relative">
        <img
          src={topic.img}
          alt={topic.title}
          title={`${topic.title} · 1黑料网直达`}
          className="w-full aspect-[16/9] object-cover object-top"
        />
        <span className="absolute top-3 left-3 inline-flex items-center gap-1 rounded-full bg-background-50/95 px-3 py-1 text-[11px] font-bold text-primary-700">
          <i className="ri-bar-chart-2-line"></i> {topic.tag}
        </span>
      </div>
      <div className="flex flex-1 flex-col p-4 md:p-5">
        <h3 className="font-serif text-lg md:text-xl font-bold text-foreground-950 leading-snug">{topic.title}</h3>
        <p className="mt-2 text-sm leading-relaxed text-foreground-600">{topic.desc}</p>
        <div className="mt-4 flex flex-col gap-2">
          <SideOption side={topic.sideA} totalLabel={topic.totalVotes} selected={selected === "A"} onSelect={() => setSelected("A")} />
          <SideOption side={topic.sideB} totalLabel={topic.totalVotes} selected={selected === "B"} onSelect={() => setSelected("B")} />
        </div>
        <div className="mt-4 flex items-center gap-2 rounded-md bg-background-100/80 px-3 py-2.5 text-xs text-foreground-600">
          <i className="ri-chat-smile-3-line text-primary-500"></i>
          <span className="line-clamp-1">{topic.comment}</span>
        </div>
        <div className="mt-3 flex items-center justify-between text-[11px] text-foreground-500">
          <span className="flex items-center gap-1"><i className="ri-group-line"></i> {topic.totalVotes}</span>
          <span className="flex items-center gap-1.5"><i className="ri-chat-3-line"></i> 围观中 <i className="ri-arrow-right-line group-hover:translate-x-0.5 transition-transform"></i></span>
        </div>
      </div>
    </article>
  );
}

export default function PollSpecial() {
  return (
    <section className="w-full">
      <div className="mx-auto max-w-[1280px] px-4 md:px-6 py-10 md:py-16">
        <div className="flex flex-wrap items-end justify-between gap-4 mb-5 md:mb-7">
          <div>
            <div className="flex items-center gap-2 mb-3">
              <span className="h-4 w-1 rounded-full bg-accent-500"></span>
              <span className="text-xs font-semibold tracking-[0.2em] text-accent-700 uppercase">立场表达 · 投票定调</span>
            </div>
            <h2 className="font-serif text-2xl md:text-4xl font-black text-foreground-950 leading-tight">
              摆上台面，投个态度
            </h2>
          </div>
          <p className="max-w-md text-sm text-foreground-500 leading-relaxed">
            不站队、不引战。用一票表达你的观点，看大家怎么想，理性讨论才是瓜品。
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6">
          {specialTopics.map((t) => (
            <PollCard key={t.title} topic={t} />
          ))}
        </div>
      </div>
    </section>
  );
}